import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import FormField from '../components/FormField/FormField';
import { accountDeleteErrorMessage, deleteAccount } from '../utils/accountApi';
import './Account.css';

const CONFIRM_WORD = 'DELETE';

export default function Account() {
    const { user, loading, login, logout } = useAuth();
    const [confirmText, setConfirmText] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);

    const canDelete = confirmText.trim().toUpperCase() === CONFIRM_WORD && !busy;

    async function handleDelete() {
        if (!canDelete) return;
        setBusy(true);
        setError(null);
        try {
            await deleteAccount();
            setDone(true);
            // The server already cleared the cookie; drop local auth state.
            await logout();
        } catch (e) {
            setError(accountDeleteErrorMessage(e.message));
        } finally {
            setBusy(false);
        }
    }

    if (loading) {
        return (
            <div className="account page page--narrow">
                <div className="empty panel"><strong>Loading your account…</strong></div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="account page page--narrow">
                <header className="account__head">
                    <div className="eyebrow">Account</div>
                    <h1 className="account__title">Sign in first</h1>
                    <p className="account__lede">
                        You need to be signed in to manage or delete your ClassGrid account.
                    </p>
                </header>
                <button type="button" className="btn btn--primary" onClick={login}>
                    Login with IITD
                </button>
            </div>
        );
    }

    if (done) {
        return (
            <div className="account page page--narrow">
                <header className="account__head">
                    <div className="eyebrow">Account</div>
                    <h1 className="account__title">Account deleted</h1>
                    <p className="account__lede">
                        Your plan, calendar events, attendance, and reminders are gone.
                        Shared content you posted stays for other students, without your name on it.
                    </p>
                </header>
                <Link to="/calendar" className="btn">Back to calendar</Link>
            </div>
        );
    }

    return (
        <div className="account page page--narrow">
            <header className="account__head">
                <div className="eyebrow">Account</div>
                <h1 className="account__title">Your account</h1>
                <p className="account__lede">
                    Manage the IITD account linked to ClassGrid, and delete it if you no longer
                    want your data stored.
                </p>
            </header>

            <section className="account__profile panel">
                <dl className="dl">
                    <dt className="mono muted">Name</dt>
                    <dd>{user.name || '—'}</dd>
                    <dt className="mono muted">Kerberos</dt>
                    <dd className="mono">{user.kerberos || '—'}</dd>
                    <dt className="mono muted">Email</dt>
                    <dd className="mono">{user.email || '—'}</dd>
                    <dt className="mono muted">Hostel</dt>
                    <dd>{user.hostel || '—'}</dd>
                </dl>
            </section>

            <section className="account__danger panel">
                <h2 className="h2">Delete account</h2>
                <p className="account__danger-lead">
                    This erases your plan, personal calendar events, attendance marks, reminder
                    preferences, and push tokens. It cannot be undone.
                </p>
                <p className="account__danger-note muted">
                    Content you shared with other students — course calendar events, room
                    markings, course policies — stays, but your name and kerberos are removed
                    from it. Your course registrations come from IITD records, not from us, so
                    they are not affected.
                </p>

                <FormField
                    label={`Type ${CONFIRM_WORD} to confirm`}
                    htmlFor="account-confirm"
                    className="form-field--fixed"
                >
                    <input
                        id="account-confirm"
                        type="text"
                        className="field field--mono"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={CONFIRM_WORD}
                        autoComplete="off"
                        spellCheck="false"
                        disabled={busy}
                    />
                </FormField>

                {error && (
                    <p className="status status--err" role="alert">{error}</p>
                )}

                <div className="account__actions">
                    <button
                        type="button"
                        className="btn btn--danger-ghost"
                        onClick={handleDelete}
                        disabled={!canDelete}
                    >
                        {busy ? 'Deleting…' : 'Delete my account'}
                    </button>
                    <Link to="/calendar" className="btn btn--ghost">Cancel</Link>
                </div>
            </section>

            <p className="account__footer muted">
                Read the <Link to="/privacy" className="account__link">privacy policy</Link> or
                see the data we store.
            </p>
        </div>
    );
}
